import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalFooter } from '../ui/modal';
import { Input, Textarea, Select } from '../ui/form';
import { Button } from '../ui/button';
import { 
  useCreateMatchIdentifier, 
  useUpdateMatchIdentifier,
  useMatchIdentifierTypes 
} from '../../hooks/use-match-identifiers';
import { 
  CreateMatchIdentifierRequest, 
  UpdateMatchIdentifierRequest, 
  MatchIdentifier,
  MatchingCriteria,
  ValidationRules
} from '../../types';

interface MatchIdentifierFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
  matchIdentifier?: MatchIdentifier; // If provided, we're editing; otherwise creating
}

interface FormData {
  name: string;
  description: string;
  type: string;
  // Matching criteria fields
  field: string;
  jsonPath: string;
  operator: string;
  // Validation rules
  required: boolean;
  pattern: string;
  minLength: string;
  maxLength: string;
  minValue: string;
  maxValue: string;
  allowedValues: string;
}

export function MatchIdentifierFormModal({ 
  isOpen, 
  onClose, 
  divisionId,
  matchIdentifier 
}: MatchIdentifierFormModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!matchIdentifier;

  const createMatchIdentifierMutation = useCreateMatchIdentifier();
  const updateMatchIdentifierMutation = useUpdateMatchIdentifier();
  
  const { 
    data: typesResponse,
    isLoading: isLoadingTypes
  } = useMatchIdentifierTypes(divisionId, isOpen);

  const { 
    register, 
    handleSubmit, 
    reset, 
    watch,
    formState: { errors }
  } = useForm<FormData>();

  const selectedType = watch('type');
  const selectedOperator = watch('operator');
  
  const availableTypes = typesResponse?.types || [];
  const selectedTypeInfo = availableTypes.find(t => t.type === selectedType);
  const availableOperators = selectedTypeInfo?.operators || [];

  // Reset form values when match identifier prop changes (for editing) or modal opens
  useEffect(() => {
    if (isOpen) {
      if (matchIdentifier) {
        const criteria = matchIdentifier.matchingCriteria;
        const rules = criteria?.validationRules || {};
        
        reset({
          name: matchIdentifier.name,
          description: matchIdentifier.description || '',
          type: matchIdentifier.type,
          field: criteria?.field || '',
          jsonPath: criteria?.jsonPath || '',
          operator: criteria?.operator || '',
          required: rules.required || false,
          pattern: rules.pattern || '',
          minLength: rules.minLength?.toString() || '',
          maxLength: rules.maxLength?.toString() || '',
          minValue: rules.minValue?.toString() || '',
          maxValue: rules.maxValue?.toString() || '',
          allowedValues: rules.allowedValues?.join(', ') || '',
        });
      } else {
        reset({
          name: '',
          description: '',
          type: '',
          field: '',
          jsonPath: '',
          operator: '',
          required: false,
          pattern: '',
          minLength: '',
          maxLength: '',
          minValue: '',
          maxValue: '',
          allowedValues: '',
        });
      }
    }
  }, [isOpen, matchIdentifier, reset]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Build validation rules
      const validationRules: ValidationRules = {};
      
      if (data.required) validationRules.required = true;
      if (data.pattern) validationRules.pattern = data.pattern;
      if (data.minLength) validationRules.minLength = parseInt(data.minLength);
      if (data.maxLength) validationRules.maxLength = parseInt(data.maxLength);
      if (data.minValue) validationRules.minValue = parseFloat(data.minValue);
      if (data.maxValue) validationRules.maxValue = parseFloat(data.maxValue);
      if (data.allowedValues) {
        validationRules.allowedValues = data.allowedValues
          .split(',')
          .map(v => v.trim())
          .filter(v => v);
      }

      // Build matching criteria
      const matchingCriteria: MatchingCriteria = {
        field: data.field || undefined,
        jsonPath: data.jsonPath || undefined,
        operator: data.operator || undefined,
        validationRules: Object.keys(validationRules).length > 0 ? validationRules : undefined,
      };

      const request = {
        name: data.name,
        description: data.description || undefined,
        type: data.type,
        matchingCriteria,
      };

      if (isEditing && matchIdentifier) {
        await updateMatchIdentifierMutation.mutateAsync({
          divisionId,
          identifierId: matchIdentifier.id,
          request: request as UpdateMatchIdentifierRequest,
        });
      } else {
        await createMatchIdentifierMutation.mutateAsync({
          divisionId,
          request: request as CreateMatchIdentifierRequest,
        });
      }

      onClose();
    } catch (error) {
      console.error('Failed to save match identifier:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isEditing ? 'Edit Match Identifier' : 'Create Match Identifier';
  const submitLabel = isEditing ? 'Update' : 'Create';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <Input
                label="Name"
                {...register('name', { 
                  required: 'Name is required',
                  maxLength: { value: 200, message: 'Name must be less than 200 characters' }
                })}
                error={errors.name?.message}
                placeholder="Enter match identifier name"
              />

              <Textarea
                label="Description"
                {...register('description', {
                  maxLength: { value: 1000, message: 'Description must be less than 1000 characters' }
                })}
                error={errors.description?.message}
                placeholder="Enter description (optional)"
                rows={3}
              />

              <Select
                label="Type"
                {...register('type', { required: 'Type is required' })}
                error={errors.type?.message}
                disabled={isLoadingTypes}
              >
                <option value="">Select type...</option>
                {availableTypes.map((type) => (
                  <option key={type.type} value={type.type}>
                    {type.name} - {type.description}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Matching Criteria */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">
              Matching Criteria
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Field"
                {...register('field')}
                error={errors.field?.message}
                placeholder="Field name"
              />

              <Input
                label="JSON Path"
                {...register('jsonPath')}
                error={errors.jsonPath?.message}
                placeholder="$.path.to.field"
              />

              <Select
                label="Operator"
                {...register('operator')}
                error={errors.operator?.message}
                disabled={!selectedType}
                className="md:col-span-2"
              >
                <option value="">Select operator...</option>
                {availableOperators.map((op) => (
                  <option key={op.operator} value={op.operator}>
                    {op.name} - {op.description}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Validation Rules */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">
              Validation Rules
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('required')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                />
                <label className="ml-2 block text-sm text-secondary-900 dark:text-white">
                  Required field
                </label>
              </div>

              <Input
                label="Pattern (Regex)"
                {...register('pattern')}
                error={errors.pattern?.message}
                placeholder="^[A-Z0-9]+$"
              />

              <Input
                label="Min Length"
                type="number"
                {...register('minLength')}
                error={errors.minLength?.message}
                placeholder="0"
              />

              <Input
                label="Max Length"
                type="number"
                {...register('maxLength')}
                error={errors.maxLength?.message}
                placeholder="100"
              />

              <Input
                label="Min Value"
                type="number"
                step="any"
                {...register('minValue')}
                error={errors.minValue?.message}
                placeholder="0"
              />

              <Input
                label="Max Value"
                type="number"
                step="any"
                {...register('maxValue')}
                error={errors.maxValue?.message}
                placeholder="1000"
              />

              <div className="md:col-span-2">
                <Input
                  label="Allowed Values"
                  {...register('allowedValues')}
                  error={errors.allowedValues?.message}
                  placeholder="value1, value2, value3"
                  helperText="Comma-separated list of allowed values"
                />
              </div>
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : submitLabel}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
